import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';
import { CompanyAccessKeyDto } from '@cargovibe/shared-types';

const DRIVER_APP_URL: string = 'https://app.cargovibe.de/';

interface ActivationKeyBadgeProps {
  activationKey: CompanyAccessKeyDto;
}

export function ActivationKeyBadge({
  activationKey,
}: Readonly<ActivationKeyBadgeProps>): React.ReactElement {
  const { t } = useTranslation();
  const { isCopied, copy } = useCopyToClipboard();

  const handleCopyClick = (): void => {
    copy(activationKey.key);
  };

  const copyIcon: React.ReactNode = isCopied ? (
    <Check size={20} aria-label={t('users.activationKeyCopied')} />
  ) : (
    <Copy size={20} />
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleCopyClick}
          className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 px-5 py-3 text-base font-semibold text-primary transition-colors hover:bg-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <span>
            {t('users.activationKeyLabel')} {activationKey.key}
          </span>
          {copyIcon}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p className="font-medium text-popover-foreground">
          {t('users.activationKeyTooltipTitle')}
        </p>
        <p className="text-muted-foreground">
          {t('users.activationKeyTooltipHint')}{' '}
          <a href={DRIVER_APP_URL} target="_blank" rel="noreferrer" className="underline">
            {DRIVER_APP_URL}
          </a>
        </p>
        <p className="text-muted-foreground">
          {t('users.activationKeyUses', {
            current: activationKey.currentUses,
            max: activationKey.maxUses,
          })}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
